import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Divider, Grid, TextField,
  Typography, Alert, Avatar, Chip
} from '@mui/material';
import { Person, Save, Lock } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getProfile, updateProfile, changePassword, USE_MOCK_DATA } from '../services/api';

export default function Profile() {
  const { user, loginUser, token } = useAuth();
  const [profile, setProfile] = useState({ fullName: '', email: '', phone: '', roleName: '', createdAt: '' });
  const [form, setForm] = useState({ fullName: '', email: '', phone: '' });
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  useEffect(() => {
    if (USE_MOCK_DATA) {
      const mockProfile = {
        id: user?.id || 1,
        fullName: user?.fullName || 'Debug User',
        email: user?.email || 'user@test.com',
        phone: user?.phone || '9999999999',
        roleName: user?.roleName || 'Admin',
        createdAt: user?.createdAt || new Date().toISOString()
      };
      setProfile(mockProfile);
      setForm({ fullName: mockProfile.fullName, email: mockProfile.email, phone: mockProfile.phone || '' });
      return;
    }
    getProfile().then(res => {
      setProfile(res.data);
      setForm({ fullName: res.data.fullName, email: res.data.email, phone: res.data.phone || '' });
    }).catch(() => {});
  }, [user]);

  const handleUpdateProfile = async () => {
    setSuccess('');
    setError('');

    if (!form.fullName.trim()) {
      setError('Full name is required');
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (form.phone && form.phone.length !== 10) {
      setError('Phone number must be 10 digits');
      return;
    }

    if (USE_MOCK_DATA) {
      setProfile(prev => ({ ...prev, fullName: form.fullName, email: form.email, phone: form.phone }));
      loginUser(token, { ...user, fullName: form.fullName, email: form.email, phone: form.phone });
      setSuccess('Profile updated successfully');
      return;
    }

    try {
      await updateProfile({ fullName: form.fullName, email: form.email, phone: form.phone || null });
      setProfile(prev => ({ ...prev, fullName: form.fullName, email: form.email, phone: form.phone }));
      loginUser(token, { ...user, fullName: form.fullName, email: form.email, phone: form.phone });
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data || 'Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    setPwSuccess('');
    setPwError('');

    if (!passwordForm.newPassword) {
      setPwError('Please enter a new password');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPwError('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPwError('Passwords do not match');
      return;
    }

    if (USE_MOCK_DATA) {
      setPwSuccess('Password changed successfully');
      setPasswordForm({ newPassword: '', confirmPassword: '' });
      return;
    }

    try {
      await changePassword({ newPassword: passwordForm.newPassword });
      setPwSuccess('Password changed successfully');
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwError(err.response?.data || 'Failed to change password');
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>My Profile</Typography>

      <Grid container spacing={3}>
        {/* Profile Info Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: 32 }}>
                {profile.fullName?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Typography variant="h6">{profile.fullName}</Typography>
              <Typography variant="body2" color="text.secondary">{profile.email}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{profile.phone}</Typography>
              <Chip label={profile.roleName} color="primary" size="small" sx={{ mt: 1.5 }} />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1.5 }}>
                Member since {new Date(profile.createdAt).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Edit Profile */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person /> Edit Profile
              </Typography>
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <TextField fullWidth label="Full Name" margin="normal" required
                value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
              <TextField fullWidth label="Email" type="email" margin="normal"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <TextField fullWidth label="Phone Number" type="tel" margin="normal"
                value={form.phone}
                onChange={(e) => { const val = e.target.value.replace(/\D/g, '').slice(0, 10); setForm({ ...form, phone: val }); }}
                inputProps={{ maxLength: 10 }}
                error={form.phone.length > 0 && form.phone.length !== 10}
                helperText={form.phone.length > 0 && form.phone.length !== 10 ? 'Must be 10 digits' : ''} />

              <Button variant="contained" startIcon={<Save />} onClick={handleUpdateProfile} sx={{ mt: 2 }}>
                Save Changes
              </Button>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock /> Change Password
              </Typography>
              {pwSuccess && <Alert severity="success" sx={{ mb: 2 }}>{pwSuccess}</Alert>}
              {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}

              <TextField fullWidth label="New Password" type="password" margin="normal"
                value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} />
              <TextField fullWidth label="Confirm New Password" type="password" margin="normal"
                value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} />

              <Button variant="contained" color="warning" startIcon={<Lock />} onClick={handleChangePassword} sx={{ mt: 2 }}>
                Change Password
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
